// src/components/auth/signup-form.tsx
'use client';

import { useState, type FormEvent } from 'react';
import Link from 'next/link';
import { useRouter, useParams } from 'next/navigation';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, UserPlus, Loader2, Briefcase } from 'lucide-react';
import { auth, db } from '@/lib/firebase';
import { createUserWithEmailAndPassword as firebaseCreateUser, updateProfile } from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import type { FirebaseError } from 'firebase/app';
import { z } from 'zod';
import type { Dictionary, Locale } from '@/lib/i18n';
import { GoogleSignInButton } from './google-signin-button';
import { MagicLinkForm } from './magic-link-form';
import { sendCustomVerificationEmail } from '@/lib/actions/email-actions';

const PFX_SIGNUP = "[SignUpForm.tsx]";

const SignUpSchemaClient = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters."}),
  email: z.string().email({ message: "Invalid email address." }),
  password: z.string().min(6, { message: "Password must be at least 6 characters." }),
  confirmPassword: z.string().min(6, { message: "Password must be at least 6 characters." }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match.",
  path: ["confirmPassword"], 
});

type SignUpFormClientState = {
  errors?: {
    name?: string[];
    email?: string[];
    password?: string[];
    confirmPassword?: string[];
    generic?: string[];
  };
  message?: string | null;
  isLoading: boolean;
};

export function SignUpForm({ dictionary }: { dictionary: Dictionary['auth']['signUp'] }) {
  const router = useRouter();
  const params = useParams();
  const lang = (params.lang || 'en-US') as Locale;
  const [formState, setFormState] = useState<SignUpFormClientState>({ isLoading: false });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log(`${PFX_SIGNUP} handleSubmit initiated.`);
    setFormState({ isLoading: true, errors: {}, message: null });

    const validatedFields = SignUpSchemaClient.safeParse({ name, email, password, confirmPassword });

    if (!validatedFields.success) {
      console.warn(`${PFX_SIGNUP} Validation failed. Errors:`, validatedFields.error.flatten().fieldErrors);
      setFormState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: 'Invalid fields. Sign up failed.',
        isLoading: false,
      });
      return;
    }
    console.log(`${PFX_SIGNUP} Validation successful. Data - Name: ${validatedFields.data.name}, Email: ${validatedFields.data.email}`);

    try {
      console.log(`${PFX_SIGNUP} PRE-CREATE_USER: Attempting Firebase Auth user creation for email: ${validatedFields.data.email}`);
      const userCredential = await firebaseCreateUser(auth, validatedFields.data.email, validatedFields.data.password);
      
      console.log(`${PFX_SIGNUP} POST-CREATE_USER: Firebase Auth user CREATED successfully.`);
      const user = userCredential.user;

      if (user) {
        console.log(`${PFX_SIGNUP} PRE-UPDATE_PROFILE: Attempting to update Firebase Auth profile for UID: ${user.uid} with displayName: ${validatedFields.data.name}`);
        await updateProfile(user, { displayName: validatedFields.data.name });
        console.log(`${PFX_SIGNUP} POST-UPDATE_PROFILE: Firebase Auth profile UPDATED successfully for UID: ${user.uid}`);
        
        console.log(`${PFX_SIGNUP} PRE-FIRESTORE_WRITE: Preparing to write to Firestore for UID: ${user.uid}`);
        const userDocRef = doc(db, 'users', user.uid);
        const userDataPayload = {
          uid: user.uid,
          name: validatedFields.data.name,
          email: validatedFields.data.email.toLowerCase(),
          createdAt: Timestamp.now(),
          preferredLanguage: lang,
          onboardingCompleted: false, 
          emailVerified: false, // Set to false initially
        };
        await setDoc(userDocRef, userDataPayload);
        console.log(`${PFX_SIGNUP} POST-FIRESTORE_WRITE: Firestore document for user CREATED successfully.`);

        // Call the server action to send the custom verification email
        console.log(`${PFX_SIGNUP} PRE-SEND_CUSTOM_VERIFICATION: Calling server action to send email.`);
        const emailResult = await sendCustomVerificationEmail(user.uid, validatedFields.data.email, validatedFields.data.name, lang);
        console.log(`${PFX_SIGNUP} POST-SEND_CUSTOM_VERIFICATION: Server action finished. Result:`, emailResult);

      } else {
        console.error(`${PFX_SIGNUP} CRITICAL: userCredential.user is null after successful Firebase Auth creation.`);
        throw new Error("User creation resulted in null user object from Firebase Auth.");
      }

      console.log(`${PFX_SIGNUP} Sign-up process complete. Redirecting to /${lang}/signin with verification message...`);
      setFormState({ isLoading: false, message: dictionary.successMessage });
      // Redirect to signin page with a status query param to show the verification message
      router.push(`/${lang}/signin?status=verification-sent`);

    } catch (e) {
      const error = e as FirebaseError;
      console.error(`${PFX_SIGNUP} ERROR during sign up process. Code: ${error.code}, Message: ${error.message}`);
      
      let genericErrorMessage = dictionary.genericError || 'Sign up failed. Please try again.';
      let fieldErrors: SignUpFormClientState["errors"] = {};

      if (error.code === 'auth/email-already-in-use') {
        genericErrorMessage = dictionary.emailInUseError || 'This email address is already in use.';
        fieldErrors.email = [genericErrorMessage];
      } else if (error.code === 'auth/invalid-email') {
        genericErrorMessage = dictionary.invalidEmailError || 'The email address is not valid.';
        fieldErrors.email = [genericErrorMessage];
      } else if (error.code === 'auth/weak-password') {
        genericErrorMessage = dictionary.weakPasswordError || 'The password is too weak.';
        fieldErrors.password = [genericErrorMessage];
      } else {
         fieldErrors.generic = [genericErrorMessage];
      }
       setFormState({
        errors: fieldErrors,
        message: null,
        isLoading: false,
      });
    }
  };

  return (
    <Card className="shadow-2xl">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">{dictionary.title}</CardTitle>
        <CardDescription>{dictionary.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
            {formState.errors?.generic && (
                <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{dictionary.errorTitle || 'Error'}</AlertTitle>
                <AlertDescription>{formState.errors.generic.join(', ')}</AlertDescription>
                </Alert>
            )}
            {formState.message && !formState.errors?.generic && (
                <Alert>
                    <AlertTitle>Info</AlertTitle>
                    <AlertDescription>{formState.message}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-2">
                <Label htmlFor="name">{dictionary.nameLabel}</Label>
                <Input
                id="name"
                name="name"
                type="text"
                placeholder="John Doe"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-describedby="name-error"
                disabled={formState.isLoading}
                />
                {formState.errors?.name && (
                <div id="name-error" aria-live="polite" className="text-sm text-destructive">
                    {formState.errors.name.map((error: string) => (
                    <p key={error}>{error}</p>
                    ))}
                </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="email">{dictionary.emailLabel}</Label>
                <Input
                id="email"
                name="email"
                type="email"
                placeholder="name@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby="email-error"
                disabled={formState.isLoading}
                />
                {formState.errors?.email && (
                <div id="email-error" aria-live="polite" className="text-sm text-destructive">
                    {formState.errors.email.map((error: string) => (
                    <p key={error}>{error}</p>
                    ))}
                </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="password">{dictionary.passwordLabel}</Label>
                <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby="password-error"
                disabled={formState.isLoading}
                />
                {formState.errors?.password && (
                <div id="password-error" aria-live="polite" className="text-sm text-destructive">
                    {formState.errors.password.map((error: string) => (
                    <p key={error}>{error}</p>
                    ))}
                </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="confirmPassword">{dictionary.confirmPasswordLabel}</Label>
                <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-describedby="confirmPassword-error"
                disabled={formState.isLoading}
                />
                {formState.errors?.confirmPassword && (
                <div id="confirmPassword-error" aria-live="polite" className="text-sm text-destructive">
                    {formState.errors.confirmPassword.map((error: string) => (
                    <p key={error}>{error}</p>
                    ))}
                </div>
                )}
            </div>
            <Button type="submit" className="w-full" aria-disabled={formState.isLoading} disabled={formState.isLoading}>
                {formState.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                {formState.isLoading ? dictionary.buttonProgress : dictionary.buttonText}
            </Button>
            </form>
             <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {dictionary.orContinueWith}
                </span>
              </div>
            </div>
            <GoogleSignInButton />
             <div>
                <MagicLinkForm dictionary={dictionary.magicLink} />
                <p className="text-xs text-muted-foreground mt-1 px-1">{dictionary.magicLink.description}</p>
            </div>
            <div>
                 <Button variant="outline" className="w-full" asChild>
                  <Link href={`/${lang}/login/sso`}>
                    <Briefcase className="mr-2 h-4 w-4" />
                    {dictionary.sso.triggerButton}
                  </Link>
                </Button>
                 <p className="text-xs text-muted-foreground mt-1 px-1">{dictionary.sso.description}</p>
            </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-center gap-4">
        <p className="text-sm text-muted-foreground">
          {dictionary.linkText}{' '}
          <Button variant="link" asChild className="p-0 h-auto">
            <Link href={`/${lang}/signin`}>{dictionary.linkAction}</Link>
          </Button>
        </p>
      </CardFooter>
    </Card>
  );
}
